import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  exportOwnerActivity,
  getOwnerActivityDetail,
  listOwnerActivity,
  revertOwnerAction,
  type OwnerActivityDetailResult,
  type OwnerActivityEntry,
  type OwnerActivityEnvironment,
  type OwnerActivityFilters,
  type OwnerActivityOutcome,
  type OwnerActivityScope,
  type OwnerRevertDryRunResult,
} from '../../../services/ownerConsoleService'
import { ConfirmActionModal } from '../../Common/ConfirmActionModal'
import { useNls } from '../../../nls/nlsProvider'
import type { NlsKey } from '../../../nls/strings_nls'
import { OwnerEmptyScaffold } from '../kit/OwnerEmptyScaffold'
import { OwnerCard } from '../kit/OwnerCard'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { navigateOwner, type OwnerRoute } from '../ownerRouter'
import { useRoles } from '../context/RolesContext'
import { useSessionLease, isSessionStaleError } from '../context/SessionLeaseProvider'
import {
  OWNER_ACTIVITY_KINDS,
  activityKindLabelKey,
  activityDateBoundary,
  activityDateInputValue,
  filtersForOwnerActivityView,
  ownerActivityFiltersToQuery,
  ownerActivityViewFromQuery,
  resolveOwnerActivityFilters,
  type OwnerActivityBuiltinView,
} from './ownerActivityModel'
import { TextInput } from '../../Common/TextInput'
import { DatePicker } from '../../Common/DatePicker'
import { Select } from '../../Common/Select'
import { MultiSelect } from '../../Common/MultiSelect'
import './ownerActivity.css'

const activityKindLabel = (kind: string, t: ReturnType<typeof useNls>['t']) => {
  const key = activityKindLabelKey(kind)
  return key ? t(key) : kind
}

type LoadState = 'loading' | 'ready' | 'error'

const BUILTIN_VIEWS: Array<{ id: OwnerActivityBuiltinView; labelKey: NlsKey }> = [
  { id: 'all', labelKey: 'NLSOwnerActivityViewAll' },
  { id: 'mine-7d', labelKey: 'NLSOwnerActivityViewMine' },
  { id: 'destructive-24h', labelKey: 'NLSOwnerActivityViewDestructive' },
  { id: 'breakglass', labelKey: 'NLSOwnerActivityViewBreakglass' },
  { id: 'failed', labelKey: 'NLSOwnerActivityViewFailed' },
  { id: 'reverts', labelKey: 'NLSOwnerActivityViewReverts' },
]

const OUTCOMES: OwnerActivityOutcome[] = ['SUCCESS', 'FAILED', 'PREVIEW', 'UNKNOWN']
const ENVIRONMENTS: OwnerActivityEnvironment[] = ['sit', 'dev', 'prod', 'unknown']
const SCOPES: Array<'all' | OwnerActivityScope> = ['all', 'global', 'workspace', 'user', 'breakglass', 'unknown']

const formatDate = (value: string | undefined) => {
  if (!value) return '—'
  const millis = Date.parse(value)
  return Number.isFinite(millis) ? new Date(millis).toLocaleString() : '—'
}

const technicalId = (value: string | null | undefined) => value || '—'

const activityHelp = (t: ReturnType<typeof useNls>['t']): [string, string, string] => [
  t('NLSOwnerActivityHelpOne'),
  t('NLSOwnerActivityHelpTwo'),
  t('NLSOwnerActivityHelpThree'),
]

const formatExportFilename = (d = new Date()) => {
  const pad = (n: number) => String(n).padStart(2, '0')
  const yyyy = d.getFullYear()
  const MM = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const HH = pad(d.getHours())
  const mm = pad(d.getMinutes())
  return `owner-activity-${yyyy}${MM}${dd}-${HH}${mm}.csv`
}

export const OwnerActivityPanel = ({ route }: { route: Extract<OwnerRoute, { view: 'activity' | 'activity-detail' }> }) => {
  const { t } = useNls()
  const { role } = useRoles()
  const { triggerSessionStale } = useSessionLease()
  const queryKey = JSON.stringify(route.query ?? {})
  const stableQuery = useMemo(() => JSON.parse(queryKey) as NonNullable<OwnerRoute['query']>, [queryKey])
  const filterResolution = useMemo(() => {
    try {
      return { filters: resolveOwnerActivityFilters(stableQuery), error: null as string | null }
    } catch {
      return {
        filters: {} as OwnerActivityFilters,
        error: t('NLSOwnerActivityInvalidFilters'),
      }
    }
  }, [stableQuery, t])
  const filters = filterResolution.filters
  const activeView = ownerActivityViewFromQuery(route.query)
  const [actorDraft, setActorDraft] = useState('')
  const [rows, setRows] = useState<OwnerActivityEntry[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [listState, setListState] = useState<LoadState>('loading')
  const [listError, setListError] = useState<string | null>(null)
  const [listRetry, setListRetry] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  const [detail, setDetail] = useState<OwnerActivityDetailResult | null>(null)
  const [detailState, setDetailState] = useState<LoadState>('ready')
  const [detailError, setDetailError] = useState<string | null>(null)
  const [detailRetry, setDetailRetry] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState(false)
  const [exportMessage, setExportMessage] = useState<string | null>(null)
  const listRequest = useRef(0)
  const detailRequest = useRef(0)

  const handleExportCsv = async () => {
    setExporting(true)
    setExportError(false)
    setExportMessage(null)
    try {
      const exportFilters: OwnerActivityFilters = {}
      if (filters.from !== undefined) exportFilters.from = filters.from
      if (filters.to !== undefined) exportFilters.to = filters.to
      if (filters.actor !== undefined) exportFilters.actor = filters.actor
      if (filters.kinds !== undefined) exportFilters.kinds = filters.kinds
      if (filters.outcomes !== undefined) exportFilters.outcomes = filters.outcomes
      if (filters.scope !== undefined) exportFilters.scope = filters.scope
      if (filters.environments !== undefined) exportFilters.environments = filters.environments
      const res = await exportOwnerActivity(exportFilters)

      const blob = new Blob(['\uFEFF' + res.csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', formatExportFilename())
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => URL.revokeObjectURL(url), 1000)

      if (res.truncated) {
        setExportMessage(t('NLSOwnerActivityExportTruncated'))
      } else {
        setExportMessage(t('NLSOwnerActivityExportSuccess', { count: res.rowCount }))
      }
    } catch (err) {
      setExportError(true)
      if (isSessionStaleError(err)) {
        triggerSessionStale()
        setExportMessage(t('NLSOwnerActivityExportError'))
      } else {
        setExportMessage(err instanceof Error ? err.message : t('NLSOwnerActivityExportError'))
      }
    } finally {
      setExporting(false)
    }
  }

  useEffect(() => {
    if (role !== 'founder' || filterResolution.error) return
    if ((activeView === 'mine-7d' || activeView === 'destructive-24h') && (!stableQuery.from || !stableQuery.to)) {
      navigateOwner({ ...route, query: ownerActivityFiltersToQuery(filters, stableQuery, activeView) })
    }
  }, [activeView, filterResolution.error, filters, role, route, stableQuery])

  useEffect(() => {
    // Reset request state when the externally controlled route/filter changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoadingMore(false)
    if (role !== 'founder' || filterResolution.error) {
      setListState(filterResolution.error ? 'error' : 'ready')
      setListError(filterResolution.error)
      setRows([])
      setNextCursor(null)
      return
    }
    const requestId = ++listRequest.current
    setListState('loading')
    setListError(null)
    setRows([])
    setNextCursor(null)
    void listOwnerActivity({ ...filters, pageSize: 50 }).then((result) => {
      if (requestId !== listRequest.current) return
      setRows(result.rows)
      setNextCursor(result.nextCursor)
      setListState('ready')
    }).catch((error) => {
      if (requestId !== listRequest.current) return
      if (isSessionStaleError(error)) {
        triggerSessionStale()
        setListError(t('NLSOwnerActivityLoadError'))
      } else {
        setListError(error instanceof Error ? error.message : t('NLSOwnerActivityLoadError'))
      }
      setListState('error')
    })
    return () => {
      if (requestId === listRequest.current) listRequest.current += 1
    }
  }, [filterResolution.error, filters, listRetry, role, t, triggerSessionStale])

  useEffect(() => {
    if (role !== 'founder' || route.view !== 'activity-detail') {
      // Clear a completed/invalidated external detail request on route or role change.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDetail(null)
      setDetailState('ready')
      setDetailError(null)
      return
    }
    const requestId = ++detailRequest.current
    setDetail(null)
    setDetailState('loading')
    setDetailError(null)
    void getOwnerActivityDetail(route.actionId).then((result) => {
      if (requestId !== detailRequest.current) return
      setDetail(result)
      setDetailState('ready')
    }).catch((error) => {
      if (requestId !== detailRequest.current) return
      if (isSessionStaleError(error)) {
        triggerSessionStale()
        setDetailError(t('NLSOwnerActivityDetailError'))
      } else {
        setDetailError(error instanceof Error ? error.message : t('NLSOwnerActivityDetailError'))
      }
      setDetailState('error')
    })
    return () => {
      if (requestId === detailRequest.current) detailRequest.current += 1
    }
  }, [detailRetry, role, route, t, triggerSessionStale])

  const updateFilters = useCallback((patch: Partial<OwnerActivityFilters>) => {
    const next = { ...filters, ...patch }
    navigateOwner({ view: 'activity', query: ownerActivityFiltersToQuery(next, route.query) })
  }, [filters, route.query])

  const applyView = useCallback((view: OwnerActivityBuiltinView) => {
    const viewFilters = filtersForOwnerActivityView(view, new Date())
    navigateOwner({ view: 'activity', query: ownerActivityFiltersToQuery(viewFilters, route.query, view) })
  }, [route.query])

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return
    const requestId = listRequest.current
    setLoadingMore(true)
    setListError(null)
    try {
      const result = await listOwnerActivity({ ...filters, pageSize: 50, cursor: nextCursor })
      if (requestId !== listRequest.current) return
      setRows((current) => {
        const seen = new Set(current.map((entry) => entry.id))
        return [...current, ...result.rows.filter((entry) => !seen.has(entry.id))]
      })
      setNextCursor(result.nextCursor)
    } catch (error) {
      if (requestId === listRequest.current) {
        if (isSessionStaleError(error)) {
          triggerSessionStale()
          setListError(t('NLSOwnerActivityLoadError'))
        } else {
          setListError(error instanceof Error ? error.message : t('NLSOwnerActivityLoadError'))
        }
      }
    } finally {
      if (requestId === listRequest.current) setLoadingMore(false)
    }
  }, [filters, loadingMore, nextCursor, t, triggerSessionStale])

  const columns = useMemo<ColumnDef<OwnerActivityEntry>[]>(() => [
    {
      key: 'action',
      header: t('NLSOwnerActivityColumnAction'),
      width: 320,
      render: (entry) => (
        <div className="ownerActivityPrimary">
          <strong>{entry.verb}</strong>
          <span>{entry.subject.label}</span>

        </div>
      ),
    },
    {
      key: 'actor',
      header: t('NLSOwnerActivityColumnActor'),
      width: 210,
      render: (entry) => <div className="ownerActivityActor"><span>{entry.actor.label}</span></div>,
    },
    {
      key: 'outcome',
      header: t('NLSOwnerActivityColumnOutcome'),
      width: 110,
      render: (entry) => <span className={`ownerActivityOutcome ownerActivityOutcome--${entry.outcome.toLowerCase()}`}>{t(`NLSOwnerActivityOutcome${entry.outcome}` as NlsKey)}</span>,
    },
    {
      key: 'environment',
      header: t('NLSOwnerActivityColumnEnvironment'),
      width: 100,
      render: (entry) => <span className="ownerActivityEnv">{entry.environment}</span>,
    },
    {
      key: 'createdAt',
      header: t('NLSOwnerActivityColumnTime'),
      width: 190,
      accessor: (entry) => entry.createdAt?.iso,
      render: (entry) => formatDate(entry.createdAt?.iso),
    },
  ], [t])

  if (role !== 'founder') {
    return (
      <OwnerEmptyScaffold
        title={t('NLSOwnerActivityAccessDeniedTitle')}
        body={t('NLSOwnerActivityAccessDeniedBody')}
        howDataGetsHere={activityHelp(t)}
      />
    )
  }

  return (
    <section className="ownerActivityPage" aria-label={t('NLSOwnerActivityTitle')}>
      <header className="ownerActivityHeader">
        <div>
          <h1>{t('NLSOwnerActivityTitle')}</h1>
          <p>{t('NLSOwnerActivitySubtitle')}</p>
        </div>
        <button type="button" className="ownerActivityButton" onClick={() => setListRetry((value) => value + 1)}>
          {t('NLSOwnerActivityRetry')}
        </button>
      </header>

      <div className="ownerActivityLayout">
        <aside className="ownerActivityFilters" aria-label={t('NLSOwnerActivityFilters')}>
          <div className="ownerActivityViewList" role="group" aria-label={t('NLSOwnerActivityViews')}>
            {BUILTIN_VIEWS.map((view) => (
              <button
                key={view.id}
                type="button"
                className={activeView === view.id ? 'ownerActivityView--active' : ''}
                onClick={() => applyView(view.id)}
              >
                {t(view.labelKey)}
              </button>
            ))}
          </div>

          <div><span>{t('NLSOwnerActivityFrom')}</span><DatePicker ariaLabel={t('NLSOwnerActivityFrom')} value={activityDateInputValue(filters.from)} onChange={(value) => updateFilters({ from: activityDateBoundary(value, 'from') })} /></div>
          <div><span>{t('NLSOwnerActivityTo')}</span><DatePicker ariaLabel={t('NLSOwnerActivityTo')} value={activityDateInputValue(filters.to)} onChange={(value) => updateFilters({ to: activityDateBoundary(value, 'to') })} /></div>
          <Select ariaLabel={t('NLSOwnerActivityActor')} value={filters.actor ?? 'all'} onChange={(actor) => updateFilters({ actor })} options={[
            { value: 'all', label: t('NLSOwnerActivityActorAll') },
            { value: 'self', label: t('NLSOwnerActivityActorSelf') },
            ...(filters.actor && !['all', 'self'].includes(filters.actor) ? [{ value: filters.actor, label: filters.actor }] : []),
          ]} />
          <form onSubmit={(event) => { event.preventDefault(); updateFilters({ actor: actorDraft || 'all' }) }}>
            <TextInput aria-label={t('NLSOwnerActivityExactActor')} placeholder={t('NLSOwnerActivityExactActor')} value={actorDraft} onChange={setActorDraft} />
            <button className="ownerActivityButton" type="submit">{t('NLSOwnerActivityApplyActor')}</button>
          </form>
          <MultiSelect label={t('NLSOwnerActivityKinds')} ariaLabel={t('NLSOwnerActivityKinds')} use="filter" selectedValues={filters.kinds ?? []} onChange={(kinds) => updateFilters({ kinds })} options={[...new Set([...OWNER_ACTIVITY_KINDS, ...(filters.kinds ?? [])])].map((kind) => ({ value: kind, label: activityKindLabel(kind, t) }))} />
          <Select ariaLabel={t('NLSOwnerActivityScope')} value={filters.scope ?? 'all'} onChange={(scope) => updateFilters({ scope })} options={SCOPES.map((scope) => ({ value: scope, label: t(`NLSOwnerActivityScope${scope}` as NlsKey) }))} />
          <MultiSelect label={t('NLSOwnerActivityOutcomes')} ariaLabel={t('NLSOwnerActivityOutcomes')} use="filter" selectedValues={filters.outcomes ?? []} onChange={(outcomes) => updateFilters({ outcomes })} options={OUTCOMES.map((outcome) => ({ value: outcome, label: t(`NLSOwnerActivityOutcome${outcome}` as NlsKey) }))} />
          <MultiSelect label={t('NLSOwnerActivityEnvironments')} ariaLabel={t('NLSOwnerActivityEnvironments')} use="filter" selectedValues={filters.environments ?? []} onChange={(environments) => updateFilters({ environments })} options={ENVIRONMENTS.map((environment) => ({ value: environment, label: t(`NLSOwnerActivityEnv${environment}` as NlsKey) }))} />
          <button
            type="button"
            className="ownerActivityButton"
            disabled={exporting}
            onClick={handleExportCsv}
          >
            {exporting ? t('NLSOwnerActivityExporting') : t('NLSOwnerActivityExportCsv')}
          </button>
          <button type="button" className="ownerActivityButton" onClick={() => applyView('all')}>{t('NLSOwnerActivityReset')}</button>
          {exportMessage ? (
            <p className={exportError ? 'ownerActivityInlineError' : 'ownerActivityExportMessage'} role={exportError ? 'alert' : 'status'}>
              {exportMessage}
            </p>
          ) : null}
        </aside>

        <div className="ownerActivityList">
          {listState === 'loading' ? (
            <div className="ownerActivityState" role="status">{t('NLSOwnerActivityLoading')}</div>
          ) : listState === 'error' ? (
            <OwnerEmptyScaffold
              title={filterResolution.error ? t('NLSOwnerActivityInvalidFilters') : t('NLSOwnerActivityLoadError')}
              body={listError ?? t('NLSOwnerActivityLoadError')}
              howDataGetsHere={activityHelp(t)}
              configLabel={filterResolution.error ? t('NLSOwnerActivityReset') : t('NLSOwnerActivityRetry')}
              onConfigClick={() => filterResolution.error ? applyView('all') : setListRetry((value) => value + 1)}
            />
          ) : (
            <>
              <OwnerTable
                data={rows}
                columns={columns}
                viewKey="owner-activity"
                getRowId={(entry) => entry.id}
                pageSize={Math.max(1, rows.length)}
                onRowClick={(entry) => navigateOwner({ view: 'activity-detail', actionId: entry.id, query: route.query })}
                emptyState={<OwnerEmptyScaffold title={t(nextCursor ? 'NLSOwnerActivityPartialTitle' : 'NLSOwnerActivityEmptyTitle')} body={t(nextCursor ? 'NLSOwnerActivityPartialBody' : 'NLSOwnerActivityEmptyBody')} howDataGetsHere={activityHelp(t)} />}
              />
              {listError ? <div className="ownerActivityInlineError" role="alert">{listError}</div> : null}
              {nextCursor ? (
                <button type="button" className="ownerActivityButton ownerActivityLoadMore" disabled={loadingMore} onClick={() => void loadMore()}>
                  {loadingMore ? t('NLSOwnerActivityLoading') : t('NLSOwnerActivityLoadMore')}
                </button>
              ) : null}
            </>
          )}
        </div>

        {route.view === 'activity-detail' ? (
          <aside className="ownerActivityDetail" aria-label={t('NLSOwnerActivityDetail')}>
            <button type="button" className="ownerActivityClose" onClick={() => navigateOwner({ view: 'activity', query: route.query })}>
              {t('NLSOwnerActivityCloseDetail')}
            </button>
            {detailState === 'loading' ? (
              <div className="ownerActivityState" role="status">{t('NLSOwnerActivityLoadingDetail')}</div>
            ) : detailState === 'error' ? (
              <OwnerEmptyScaffold
                title={t('NLSOwnerActivityDetailError')}
                body={detailError ?? t('NLSOwnerActivityDetailError')}
                howDataGetsHere={activityHelp(t)}
                configLabel={t('NLSOwnerActivityRetry')}
                onConfigClick={() => setDetailRetry((value) => value + 1)}
              />
            ) : detail ? (
              <OwnerActivityDetailCard
                key={detail.row.id}
                detail={detail}
                query={route.query}
                onRefresh={() => {
                  setDetailRetry((value) => value + 1)
                  setListRetry((value) => value + 1)
                }}
              />
            ) : null}
          </aside>
        ) : null}
      </div>
    </section>
  )
}

const formatSnapshotString = (snap: Record<string, unknown> | null | undefined) => {
  if (!snap || Object.keys(snap).length === 0) return '—'
  return Object.entries(snap).map(([k, v]) => `${k}: ${String(v)}`).join(', ')
}

const OwnerActivityDetailCard = ({
  detail,
  query,
  onRefresh,
}: {
  detail: OwnerActivityDetailResult
  query?: OwnerRoute['query']
  onRefresh: () => void
}) => {
  const { t } = useNls()
  const { triggerSessionStale } = useSessionLease()
  const { row: entry, revert } = detail
  const [checkingDryRun, setCheckingDryRun] = useState(false)
  const [dryRunResult, setDryRunResult] = useState<OwnerRevertDryRunResult | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [revertingBusy, setRevertingBusy] = useState(false)
  const [inlineMessage, setInlineMessage] = useState<string | null>(null)

  const handleRevertClick = async () => {
    setCheckingDryRun(true)
    setInlineMessage(null)
    try {
      const res = await revertOwnerAction({ actionId: entry.id, dryRun: true })
      if ('blockedReason' in res && res.blockedReason) {
        setInlineMessage(res.blockedReason)
      } else {
        setDryRunResult(res as OwnerRevertDryRunResult)
        setModalOpen(true)
      }
    } catch (err) {
      if (isSessionStaleError(err)) {
        triggerSessionStale()
        setInlineMessage(t('NLSOwnerActivityRevertError'))
      } else {
        setInlineMessage(err instanceof Error ? err.message : t('NLSOwnerActivityRevertError'))
      }
    } finally {
      setCheckingDryRun(false)
    }
  }

  const handleConfirmRevert = async () => {
    setRevertingBusy(true)
    setInlineMessage(null)
    try {
      await revertOwnerAction({ actionId: entry.id })
      setModalOpen(false)
      onRefresh()
    } catch (err) {
      setModalOpen(false)
      if (isSessionStaleError(err)) {
        triggerSessionStale()
        setInlineMessage(t('NLSOwnerActivityRevertError'))
      } else {
        setInlineMessage(err instanceof Error ? err.message : t('NLSOwnerActivityRevertError'))
      }
    } finally {
      setRevertingBusy(false)
    }
  }

  const snapshotRows = (value: OwnerActivityEntry['before']) => value
    ? Object.entries(value).map(([key, item]) => <div key={key}><dt>{key}</dt><dd>{String(item)}</dd></div>)
    : <p>{t('NLSOwnerActivityNoSnapshot')}</p>

  const confirmBody = dryRunResult
    ? `${t('NLSOwnerActivityRevertTarget')}: ${dryRunResult.target}\n\n${formatSnapshotString(dryRunResult.before)} → ${formatSnapshotString(dryRunResult.after)}`
    : ''

  return (
    <>
      <OwnerCard title={entry.verb} metaLine={`${entry.kind} · ${entry.outcome}`} maxBodyHeight="none">
        <dl className="ownerActivityDefinitionList">
          <div><dt>{t('NLSOwnerActivitySubject')}</dt><dd>{entry.subject.label}<small>{technicalId(entry.subject.workspaceId ?? entry.subject.userId)}</small></dd></div>
          <div><dt>{t('NLSOwnerActivityActor')}</dt><dd>{entry.actor.label}<small>{entry.actor.id}</small></dd></div>
          <div><dt>{t('NLSOwnerActivityTime')}</dt><dd>{formatDate(entry.createdAt?.iso)}</dd></div>
          <div><dt>{t('NLSOwnerActivityEnvironment')}</dt><dd>{entry.environment}</dd></div>
          <div><dt>{t('NLSOwnerActivityReason')}</dt><dd>{entry.reason || '—'}</dd></div>
          {entry.errorMessage ? <div><dt>{t('NLSOwnerActivityError')}</dt><dd>{entry.errorMessage}</dd></div> : null}
          <div><dt>{t('NLSOwnerActivityActionId')}</dt><dd><code>{entry.id}</code></dd></div>
          {entry.storedActionId ? <div><dt>{t('NLSOwnerActivityStoredId')}</dt><dd><code>{entry.storedActionId}</code></dd></div> : null}
          {revert.state === 'DONE' && revert.revertActionId ? (
            <div>
              <dt>{t('NLSOwnerActivityRevertedBy')}</dt>
              <dd>
                <button
                  type="button"
                  className="ownerActivityLinkButton"
                  onClick={() => navigateOwner({ view: 'activity-detail', actionId: revert.revertActionId!, query })}
                >
                  <code>{revert.revertActionId}</code>
                </button>
              </dd>
            </div>
          ) : null}
          {entry.metadata.revertOf ? (
            <div>
              <dt>{t('NLSOwnerActivityReverts')}</dt>
              <dd>
                <button
                  type="button"
                  className="ownerActivityLinkButton"
                  onClick={() => navigateOwner({ view: 'activity-detail', actionId: entry.metadata.revertOf!, query })}
                >
                  <code>{entry.metadata.revertOf}</code>
                </button>
              </dd>
            </div>
          ) : null}
        </dl>
        <section className="ownerActivitySnapshot"><h4>{t('NLSOwnerActivityBefore')}</h4><dl>{snapshotRows(entry.before)}</dl></section>
        <section className="ownerActivitySnapshot"><h4>{t('NLSOwnerActivityAfter')}</h4><dl>{snapshotRows(entry.after)}</dl></section>
        {revert.revertible ? (
          <div className="ownerActivityRevertSection">
            <button
              type="button"
              className="ownerActivityRevertButton"
              disabled={checkingDryRun || revertingBusy}
              onClick={handleRevertClick}
            >
              {checkingDryRun ? t('NLSOwnerActivityReverting') : t('NLSOwnerActivityRevert')}
            </button>
            {inlineMessage ? <p className="ownerActivityInlineError">{inlineMessage}</p> : null}
          </div>
        ) : inlineMessage ? (
          <div className="ownerActivityRevertSection">
            <p className="ownerActivityInlineError">{inlineMessage}</p>
          </div>
        ) : null}
        <p className="ownerActivityIntegrity">
          {entry.metadata.signaturePresent ? t('NLSOwnerActivitySignaturePresent') : t('NLSOwnerActivitySignatureMissing')}
          {' '}{t('NLSOwnerActivityIntegrityUnverified')}
        </p>
      </OwnerCard>
      {modalOpen ? (
        <ConfirmActionModal
          open={modalOpen}
          danger
          busy={revertingBusy}
          title={t('NLSOwnerActivityRevertConfirmTitle')}
          confirmLabel={t('NLSOwnerActivityRevertConfirmAction')}
          body={confirmBody}
          onClose={() => {
            if (!revertingBusy) setModalOpen(false)
          }}
          onConfirm={handleConfirmRevert}
        />
      ) : null}
    </>
  )
}
